/*
 * BFF client configuration for Admin South Downs.
 *
 * The BFF base URL is environment-scoped (development, UAT, production) and is
 * built as: hostname + a bounded-context prefix, e.g.
 * https://dev-api.visitonelink.com/mmv. The prefix segment sits immediately
 * after the hostname and the endpoint paths (/api/bff/...) are appended after
 * it — so a full URL looks like
 * https://dev-api.visitonelink.com/mmv/api/bff/tenants.
 *   - hostname → bff.hostname in this deployment's /app-config.json
 *
 * Each admin BFF operation fronts exactly one VisitOne bounded context, and the
 * BFF mounts each context under its own prefix immediately after the host (see
 * CONTEXT_PREFIXES). Every operation built so far fronts the manage-my-visit
 * context ("mmv"); the food-service, table-service, membership and campaign
 * prefixes are reserved for the operations those contexts will front as they
 * are added. Callers pass their operation's context to getBffBaseUrl so the
 * correct prefix is used; it defaults to "mmv" for the existing operations.
 *
 * Both the hostname and the x-visit-key (an environment-scoped value sent on
 * every BFF call) come from the runtime configuration loaded at startup
 * (ADR-034), so one build serves every environment. Neither has a fallback.
 */

import { getRuntimeConfig } from '@/lib/config/runtime-config'

/**
 * The VisitOne bounded contexts the admin BFF fronts. Each is mounted under its
 * own prefix immediately after the host.
 *   - mmv        → manage-my-visit (tenants, languages, Today dashboard, ...)
 *   - food       → food-service ordering
 *   - table      → table service
 *   - membership → memberships
 *   - campaign   → campaigns
 */
export type BffContext = 'mmv' | 'food' | 'table' | 'membership' | 'campaign'

/**
 * The prefix each bounded context is mounted under, inserted immediately after
 * the hostname when building the base URL. Endpoint paths are appended after it.
 */
const CONTEXT_PREFIXES: Record<BffContext, string> = {
  mmv: '/mmv',
  food: '/food',
  table: '/table',
  membership: '/membership',
  campaign: '/campaign',
}

/** Path of the tenants endpoint, appended to the resolved BFF base URL. */
export const TENANTS_PATH = '/api/bff/tenants'

/** Path of the tenant languages endpoint, appended to the resolved BFF base URL. */
export const LANGUAGES_PATH = '/api/bff/languages'

/** Path of the access decision endpoint (VIS-329): what the caller may open and change in a tenant. */
export const ACCESS_PATH = '/api/bff/access'

/** Path of the Today dashboard context endpoint (VIS-257). */
export const TODAY_CONTEXT_PATH = '/api/bff/today'

/** Path of a single Today module's data endpoint (VIS-257); the module id is appended. */
export const TODAY_MODULE_PATH = '/api/bff/today/module'

/** Path of the Today module dismissal endpoint (VIS-257). */
export const TODAY_DISMISS_PATH = '/api/bff/today/dismiss'

/** Path of the Landlord management endpoints (VIS-328); `/access` and `/{userId}` are appended. */
export const LANDLORDS_PATH = '/api/bff/landlords'

/** Path of the People & access endpoints (VIS-332), all with `?tenant=`: `/users`, `/invitations`, `/roles`, `/changes`. */
export const PEOPLE_PATH = '/api/bff/people'

/** Path of the guest endpoints (VIS-460), all with `?tenant=`: `/search`, `/lists/{list}`, `/{guestId}`, `/{guestId}/history`. */
export const GUESTS_PATH = '/api/bff/guests'

/** Path of the guest data access record (VIS-330), all with `?tenant=`: `/record`, `/record/{productionId}`, `/download`. */
export const GUEST_ACCESS_PATH = '/api/bff/guest-access'

/** Path of the signed-in invitation endpoints (VIS-332): `/details` and `/accept`, with no tenant. */
export const INVITATIONS_PATH = '/api/bff/invitations'

/** Path of the public invitation endpoints (VIS-332): `/peek` and `/create-account`, sent with no bearer token. */
export const PUBLIC_INVITATIONS_PATH = '/api/bff/public/invitations'

/**
 * Resolve the BFF base URL for the given bounded context: the configured
 * origin + the context's prefix (e.g. https://api.visitonelink.com/mmv). The
 * context defaults to "mmv" (manage-my-visit), which owns every operation
 * built so far.
 */
export function getBffBaseUrl(context: BffContext = 'mmv'): string {
  return `${getRuntimeConfig().bff.hostname}${CONTEXT_PREFIXES[context]}`
}

/** The environment-scoped x-visit-key sent with every BFF request. */
export function getVisitKey(): string {
  return getRuntimeConfig().bff.visitKey
}
